import { Button, Flex, Grid, Tabs } from '@mantine/core';
import {
  IconBrain,
  IconChartBar,
  IconCreditCard,
  IconDeviceFloppy,
  IconHistory,
  IconKey,
  IconListCheck,
  IconPalette,
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconSettings,
  IconShieldLock,
  IconTrash,
  IconTruck,
} from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import FeedBanditPanel from '../../components/FeedBandit/FeedBanditPanel';
import Metadata from '../../components/Metadata';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import DomainSuspendResumeConfirmationModal from '../../components/Modals/DomainSuspendResumeConfirmationModal';
import SettingsTabAiHubTokens from '../../components/Settings/Tabs/SettingsTabAiHubTokens';
import SettingsTabAiPrompts from '../../components/Settings/Tabs/SettingsTabAiPrompts';
import SettingsTabAnalytics from '../../components/Settings/Tabs/SettingsTabAnalytics';
import SettingsTabAuditLog from '../../components/Settings/Tabs/SettingsTabAuditLog';
import SettingsTabGeneral from '../../components/Settings/Tabs/SettingsTabGeneral';
import SettingsTabProductSync from '../../components/Settings/Tabs/SettingsTabProductSync';
import SettingsTabShipmentProvider from '../../components/Settings/Tabs/SettingsTabShipmentProvider';
import SettingsTabSecrets from '../../components/Settings/Tabs/SettingsTabSecrets';
import SettingsTabUsage from '../../components/Settings/Tabs/SettingsTabUsage';
import SettingsTabVisuals from '../../components/Settings/Tabs/SettingsTabVisuals';
import Spinner from '../../components/Spinner';
import SuperAdminArea from '../../components/SuperAdmin/SuperAdminArea';
import SuperAdminBadge from '../../components/SuperAdmin/SuperAdminBadge';
import superAdminAccents from '../../components/SuperAdmin/superAdminAccents.module.css';
import defaultEventTypes from '../../constants/eventTypes';
import { Page, SettingsDomainTabs } from '../../constants/pageTitles';
import isInvalidCronExpression from '../../helpers/cronValidator';
import { conversionTypesOf } from '../../helpers/customEventConversions';
import useFetch from '../../helpers/useFetch';
import { validateUrlProtocol } from '../../helpers/validateURL';
import { setUser } from '../../store/authSlice';
import { setDomain as setDomainRedux } from '../../store/domainSlice';

const SettingsDomainPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();

  const currentDomain = useSelector((state) => state.domain.domain);
  const currentAccount = useSelector((state) => state.account.account);
  const user = useSelector((state) => state.auth.user);

  const isSuperAdmin = user.userRoles.some((role) => role.role === 'superAdmin');

  const [domain, setDomain] = useState(currentDomain);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState(false);
  const [usage, setUsage] = useState();
  const [eventActions, setEventActions] = useState();
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [confirmDeleteTrackingOpen, setConfirmDeleteTrackingOpen] = useState(false);
  const [confirmDeleteProductsOpen, setConfirmDeleteProductsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const search = queryString.parse(location.search);
  let tab = search.tab || 'general';

  const currentDomainStringified = useMemo(() => JSON.stringify(currentDomain), [currentDomain]);
  const domainStringified = useMemo(() => JSON.stringify(domain), [domain]);
  const domainChanged = currentDomainStringified !== domainStringified;
  const accountDomains = user.userRoles.find((r) => r.account?.id === currentAccount.id).account.domains;

  useEffect(() => {
    setDomain(currentDomain);
  }, [currentDomain]);

  useEffect(() => {
    setUsage(undefined);
    setEventActions(undefined);
    setError(undefined);

    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/usage`)
      .then((res) => {
        setUsage(res);
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/eventActions`)
      .then(({ eventActions }) => {
        const defaultEventValues = new Set(defaultEventTypes.map((e) => e.value));
        const customEvents = eventActions
          .filter((action) => !defaultEventValues.has(action))
          .map((action) => {
            if (action.startsWith('Catalog trigger - ')) {
              return { label: action, value: action };
            }

            return { label: `Custom Event: ${action}`, value: action };
          });

        setEventActions([...customEvents, { label: 'Cart Checkout Completed', value: 'cartCheckoutSuccess' }]);
      })
      .catch(setError);
  }, [fetch, currentDomain.accountId, currentDomain.id]);

  const handleChange = useCallback((e) => {
    setDomain((prevDomainState) => ({
      ...prevDomainState,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const validateDomain = (domain, domainChanged) => {
    if (!domain.name) {
      return false;
    }

    if (!validateUrlProtocol(domain.url)) {
      return false;
    }

    if (!domainChanged) {
      return false;
    }

    if (isInvalidCronExpression(domain.productSyncSchedule)) {
      return false;
    }

    return true;
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!usage || parseInt(domain?.id) !== parseInt(currentDomain.id) || eventActions === undefined) {
    return <Spinner />;
  }

  const del = () => {
    if (accountDomains.length < 2) {
      return;
    }

    setError(undefined);
    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        fetch(`/api/admin/sessions/current`)
          .then((res) => {
            setConfirmDeleteOpen(false);
            dispatch(setUser(res.user));
          })
          .catch(setError);
      })
      .catch(setError);
  };

  const disableEnableDomain = () => {
    return fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        running: !currentDomain.running,
      }),
    })
      .then(({ domain }) => {
        setConfirmDisableOpen(false);
        dispatch(setDomainRedux(domain));
      })
      .catch(setError);
  };

  const deleteData = (index) => {
    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/deleteData`, {
      method: 'POST',
      body: JSON.stringify({
        index,
      }),
    })
      .then(() => {
        setConfirmDeleteTrackingOpen(false);
        setConfirmDeleteProductsOpen(false);
      })
      .catch(setError);
  };

  const submitForm = (e) => {
    e.preventDefault();
    setError(undefined);
    setSaving(true);

    // Drop incomplete conversion mappings (no type picked) so empty or
    // value-only entries aren't persisted. Read through conversionTypesOf: an
    // entry stored in the new { events: [...] } shape carries a type too, and
    // dropping it here would destroy the mapping the tab just preserved.
    const customEventConversions = Object.fromEntries(
      Object.entries(domain.customEventConversions || {}).filter(([, cfg]) => conversionTypesOf(cfg).length)
    );

    // The four columns below are retired in favor of customEventConversions
    // (magellan-api #2693-#2696) but the UI no longer edits them, so `domain`
    // still carries whatever the API returned at load time. Omit them from the
    // PUT rather than resend those stale values — if the columns still exist
    // server-side, sending them back unedited risks a compat shim mirroring
    // them over the operator's fresh customEventConversions edit in the same
    // request.
    const {
      purchaseEvents: _purchaseEvents,
      cartAddEvents: _cartAddEvents,
      cartCreateEvents: _cartCreateEvents,
      refundEvents: _refundEvents,
      ...domainWithoutRetiredArrays
    } = domain;

    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...domainWithoutRetiredArrays, customEventConversions }),
    })
      .then((result) => {
        dispatch(setDomainRedux(result.domain));
      })
      .catch(setError)
      .finally(() => setSaving(false));
  };

  const toggleIntegrationActive = () => {
    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}`, {
      method: 'PUT',
      body: JSON.stringify({ integrationActive: !domain.integrationActive }),
    })
      .then((result) => {
        dispatch(setDomainRedux(result.domain));
      })
      .catch(setError);
  };

  const setTab = (tab) => {
    navigate(location.pathname.replace(`/settings/domain`, `/settings/domain?tab=${tab}`));
  };

  let deleteButton;
  let deleteTrackingData;
  let deleteProductsData;
  let disableDomain;
  let activateDomain;
  if (accountDomains.length > 1) {
    deleteButton = (
      <Button type="button" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete Shop
      </Button>
    );
  }

  deleteTrackingData = (
    <Button type="button" color="red" onClick={() => setConfirmDeleteTrackingOpen(true)}>
      <IconTrash size={14} /> Delete Tracking Data
    </Button>
  );

  const toggleInvoicing = (
    <Button type="button" color="yellow" onClick={() => toggleIntegrationActive()}>
      {domain.integrationActive && <IconPlayerPause size={14} />} {domain.integrationActive ? 'Disable' : 'Enable'}{' '}
      Invoicing
      {!domain.integrationActive && (
        <>
          {' '}
          <IconPlayerPlay size={14} />
        </>
      )}
    </Button>
  );

  deleteProductsData = (
    <Button type="button" color="red" onClick={() => setConfirmDeleteProductsOpen(true)}>
      <IconTrash size={14} /> Delete Products
    </Button>
  );

  disableDomain = (
    <Button type="button" color="yellow" onClick={() => setConfirmDisableOpen(true)}>
      <IconPlayerPause size={14} /> Disable Domain
    </Button>
  );

  activateDomain = (
    <Button type="button" color="green" onClick={() => setConfirmDisableOpen(true)}>
      Resume Subscription <IconPlayerPlay size={14} />
    </Button>
  );

  return (
    <div>
      <Metadata title={`${Page['settings']} | ${SettingsDomainTabs[tab]}`} />
      <Grid pt={30} my={20}>
        <Grid.Col span={12} ta="left">
          <form onSubmit={(e) => submitForm(e)}>
            <Tabs value={tab} onChange={setTab} mb="md" keepMounted={false}>
              <Tabs.List>
                <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>
                  General
                </Tabs.Tab>
                <Tabs.Tab value="visuals" leftSection={<IconPalette size={16} />}>
                  Visuals
                </Tabs.Tab>
                <Tabs.Tab value="analytics" leftSection={<IconChartBar size={16} />}>
                  Events &amp; Analytics
                </Tabs.Tab>
                <Tabs.Tab value="productSync" leftSection={<IconRefresh size={16} />}>
                  Catalog Sync
                </Tabs.Tab>
                <Tabs.Tab value="usage" leftSection={<IconCreditCard size={16} />}>
                  Subscriptions & Usage
                </Tabs.Tab>
                <Tabs.Tab value="shipmentProvider" leftSection={<IconTruck size={16} />}>
                  Shipment Provider
                </Tabs.Tab>
                {isSuperAdmin && (
                  <Tabs.Tab
                    value="feedBandit"
                    leftSection={<IconListCheck size={16} />}
                    rightSection={<SuperAdminBadge iconOnly show />}
                    className={superAdminAccents.tab}
                    color="blue"
                  >
                    Feed bandit
                  </Tabs.Tab>
                )}
                {isSuperAdmin && (
                  <Tabs.Tab
                    value="aiPrompts"
                    leftSection={<IconBrain size={16} />}
                    rightSection={<SuperAdminBadge iconOnly show />}
                    className={superAdminAccents.tab}
                    color="blue"
                  >
                    AI Prompts
                  </Tabs.Tab>
                )}
                <Tabs.Tab value="aiHubTokens" leftSection={<IconKey size={16} />}>
                  AI Hub Tokens
                </Tabs.Tab>
                <Tabs.Tab value="secrets" leftSection={<IconShieldLock size={16} />}>
                  Secrets
                </Tabs.Tab>
                <Tabs.Tab value="auditLog" leftSection={<IconHistory size={16} />}>
                  Audit Log
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="general">
                <SettingsTabGeneral
                  domain={domain}
                  account={currentAccount}
                  handleChange={handleChange}
                  eventActions={eventActions}
                  isSuperAdmin={isSuperAdmin}
                />
              </Tabs.Panel>
              <Tabs.Panel value="visuals">
                <SettingsTabVisuals
                  domain={domain}
                  handleChange={handleChange}
                  eventActions={eventActions}
                  isSuperAdmin={isSuperAdmin}
                />
              </Tabs.Panel>
              <Tabs.Panel value="analytics">
                <SettingsTabAnalytics
                  domain={domain}
                  setDomain={setDomain}
                  handleChange={handleChange}
                  eventActions={eventActions}
                  isSuperAdmin={isSuperAdmin}
                />
              </Tabs.Panel>
              <Tabs.Panel value="productSync">
                <SettingsTabProductSync domain={domain} handleChange={handleChange} />
              </Tabs.Panel>
              <Tabs.Panel value="usage">
                <SettingsTabUsage
                  account={currentAccount}
                  domain={domain}
                  setDomain={setDomain}
                  usage={usage}
                  currentAccount={currentAccount}
                  isSuperAdmin={isSuperAdmin}
                />
              </Tabs.Panel>
              <Tabs.Panel value="shipmentProvider">
                <SettingsTabShipmentProvider domain={domain} handleChange={handleChange} />
              </Tabs.Panel>
              {isSuperAdmin && (
                <Tabs.Panel value="feedBandit">
                  <FeedBanditPanel domain={domain} />
                </Tabs.Panel>
              )}
              {isSuperAdmin && (
                <Tabs.Panel value="aiPrompts">
                  <SettingsTabAiPrompts domain={domain} handleChange={handleChange} />
                </Tabs.Panel>
              )}
              <Tabs.Panel value="aiHubTokens">
                <SettingsTabAiHubTokens domain={domain} handleChange={handleChange} />
              </Tabs.Panel>
              <Tabs.Panel value="secrets">
                <SettingsTabSecrets domain={domain} />
              </Tabs.Panel>
              <Tabs.Panel value="auditLog">
                <SettingsTabAuditLog domain={domain} />
              </Tabs.Panel>
            </Tabs>
            <Flex justify="space-between" align="center" mt="md">
              <Flex gap="xs" wrap="wrap" align="center">
                <SuperAdminArea show={isSuperAdmin} inline>
                  {domain.platform !== 'shopify' && (
                    <>
                      {domain.running ? disableDomain : activateDomain}
                      {toggleInvoicing}
                    </>
                  )}
                  {deleteTrackingData}
                  {deleteProductsData}
                </SuperAdminArea>
                {deleteButton}
              </Flex>
              <Flex gap="xs">
                {tab !== 'auditLog' &&
                  tab !== 'billing' &&
                  tab !== 'usage' &&
                  tab !== 'aws' &&
                  tab !== 'gcs' &&
                  tab !== 'secrets' &&
                  tab !== 'feedBandit' && (
                    <Button disabled={!validateDomain(domain, domainChanged) || saving} type="submit">
                      <IconDeviceFloppy size={14} /> {saving ? 'Saving...' : domainChanged ? 'Save' : 'Saved'}
                    </Button>
                  )}
              </Flex>
            </Flex>
          </form>
        </Grid.Col>
      </Grid>
      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={del}
        message="This action cannot be undone!"
      />

      <DomainSuspendResumeConfirmationModal
        show={confirmDisableOpen}
        running={currentDomain.running}
        onConfirm={disableEnableDomain}
        close={() => setConfirmDisableOpen(false)}
      />
      <ConfirmationModal
        show={confirmDeleteProductsOpen}
        close={() => setConfirmDeleteProductsOpen(false)}
        confirm={() => deleteData('products')}
        message={'All products will be deleted!'}
      />
      <ConfirmationModal
        show={confirmDeleteTrackingOpen}
        close={() => setConfirmDeleteTrackingOpen(false)}
        confirm={() => deleteData('profiles')}
        message={'All Events and Profiles will be deleted!'}
      />
    </div>
  );
};

export default SettingsDomainPage;
